import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, MaskedLines, Eyebrow } from "@/components/Motion";
import { img } from "@/lib/content";

const VALUES = [
  {
    name: "Confidential by Default",
    text: "Your search is never advertised without consent. We approach opportunities with discretion and represent you only where there is genuine fit.",
  },
  {
    name: "Representation, Not Transactions",
    text: "We advise on mandate quality, compensation and long-term trajectory — not just the next offer on the table.",
  },
  {
    name: "GCC Market Intelligence",
    text: "Real insight into employers, packages, relocation and career architecture across the UAE, Saudi Arabia, Qatar and the wider region.",
  },
  {
    name: "North American Standards",
    text: "Structured preparation, honest feedback and professional communication at every stage of your search.",
  },
];

const STEPS = [
  { name: "Share Your Profile", text: "Upload your resume or apply to a role. A consultant reviews every profile personally." },
  { name: "Consultative Conversation", text: "We discuss your trajectory, preferences and the realities of the GCC market." },
  { name: "Curated Opportunities", text: "You are introduced only to mandates where there is genuine alignment." },
  { name: "Offer & Beyond", text: "We support negotiation, relocation and your first months in role." },
];

const Candidates = () => (
  <>
    <Seo
      title="For Candidates — NEXREQ UAE & GCC"
      siteName="NEXREQ"
      description="Executive and professional opportunities across the UAE and GCC, with confidential representation and genuine market counsel."
    />
    <PageHero
      eyebrow="For Candidates"
      lines={["Your Career,", "Elevated."]}
      description="We represent exceptional professionals to exceptional organizations across the UAE and GCC — with discretion, honesty and real market insight."
      image={img("photo-1507679799987-c73779587ccf", 1920)}
    >
      <Reveal delay={0.75}>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/jobs" className="nx-btn-primary" data-testid="candidates-hero-jobs-btn">
            Explore Opportunities <ArrowRight size={15} />
          </Link>
          <Link to="/upload-resume" className="nx-btn-outline" data-testid="candidates-hero-resume-btn">
            Upload Your Resume
          </Link>
        </div>
      </Reveal>
    </PageHero>

    <section className="bg-obsidian py-24 lg:py-32" data-testid="candidates-values">
      <div className="nx-container">
        <Reveal>
          <Eyebrow>How We Represent You</Eyebrow>
        </Reveal>
        <h2 className="mt-6 max-w-3xl font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl">
          <MaskedLines lines={["A Search Partner,", "Not a CV Forwarding Service."]} />
        </h2>
        <div className="mt-14 grid gap-px border border-white/[0.08] bg-white/[0.06] md:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.07}>
              <div className="h-full bg-obsidian p-9" data-testid={`candidate-value-${i}`}>
                <span className="font-mono text-[10px] text-emerald-500/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-heading text-xl font-light text-white">{v.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-surface py-24 text-white lg:py-32" data-testid="candidates-process">
      <div className="nx-container grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <Eyebrow>The Journey</Eyebrow>
            </Reveal>
            <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl">
              <MaskedLines lines={["Four Steps to", "Your Next Chapter."]} />
            </h2>
            <Reveal delay={0.25}>
              <div className="mt-10 overflow-hidden">
                <img
                  src={img("photo-1573496359142-b8d87734a5a2", 1200)}
                  alt="Professional in a modern office"
                  className="aspect-[4/5] w-full object-cover grayscale-[25%]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          {STEPS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <div
                className="grid gap-3 border-t border-white/10 py-9 sm:grid-cols-[56px_1fr] sm:gap-6"
                data-testid={`candidate-step-${i}`}
              >
                <span className="font-mono text-xs text-emerald-400">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-heading text-xl font-medium lg:text-2xl">{s.name}</h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/55">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-white/10" />
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/jobs" className="nx-btn-primary" data-testid="candidates-bottom-jobs-btn">
                Find Jobs <ArrowRight size={15} />
              </Link>
              <Link to="/upload-resume" className="nx-btn-outline" data-testid="candidates-bottom-resume-btn">
                Upload Your Resume
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);

export default Candidates;
